/**
 * CR500 Phase 2B: TermsBlock
 * CR500 Phase 5B: V1 parity - same fallback chain and "details vary" rules
 * 
 * Renders the offer terms section in deal details.
 * Supports maxLines config for flip mode truncation.
 * 
 * V1 Fallback chain: raw_source_notes → fine_print → headline_mechanic
 * "Details vary" copy rule: If deal has details_vary flag, append disclaimer
 */

import React from "react";
import { FileText } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";
import { isV2DebugEnabled } from "@/lib/deal-cards/helpers/debugGuard";

/**
 * Get terms content with V1 parity fallback chain.
 * Priority: raw_source_notes > fine_print > headline_mechanic
 */
function getTermsContent(deal: Deal): string | null {
  // V1 parity: DealDetailsContent uses raw_source_notes || fine_print
  // TermsBlock Phase 2B used fine_print || headline_mechanic
  // Phase 5B: Align with V1 - raw_source_notes takes priority
  return deal.raw_source_notes || deal.fine_print || deal.headline_mechanic || null;
}

/**
 * Check if "details may vary" disclaimer should be shown.
 */
function shouldShowDetailsVaryDisclaimer(deal: Deal): boolean {
  // Check for details_vary flag or bulk deal indicators
  // Use type assertion through unknown for optional fields
  const dealAny = deal as unknown as Record<string, unknown>;
  const detailsVary = dealAny.details_vary === true;
  const isBulkDeal = dealAny.is_bulk_deal === true;
  return detailsVary || isBulkDeal;
}

export const TermsBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  const debugMode = isV2DebugEnabled();
  
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // Get terms content using V1-parity fallback chain
  const termsText = getTermsContent(deal);
  
  // If no terms content, don't render (matches V1 behavior when no data)
  // In V1: "No terms available" italic text is shown, but V2 skips entirely for cleaner look
  if (!termsText) {
    // Debug mode: show what was checked
    if (debugMode) {
      return (
        <div 
          className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs"
          data-block="terms"
          data-block-id={block.id}
        >
          <span className="font-medium text-yellow-600">[Debug] TermsBlock: No content</span>
          <div className="text-yellow-600/80 mt-1">
            Checked: raw_source_notes, fine_print, headline_mechanic
          </div>
        </div>
      );
    }
    return null;
  }

  // Get maxLines from block config (used in flip mode)
  const maxLines = block.config?.maxLines as number | undefined;
  
  // Line clamp class based on maxLines
  const clampClass = maxLines ? `line-clamp-${Math.min(maxLines, 6)}` : undefined;
  
  // Check for details vary disclaimer
  const showDetailsVary = shouldShowDetailsVaryDisclaimer(deal);

  return (
    <div 
      className="space-y-1.5"
      data-block="terms"
      data-block-id={block.id}
    >
      <h4 className="text-sm font-semibold flex items-center gap-1.5">
        <FileText className="w-4 h-4" />
        {context.labels?.terms_title || "Offer Terms"}
      </h4>
      <p className={cn(
        "text-sm text-muted-foreground",
        clampClass,
      )}>
        {termsText}
      </p>
      
      {/* V1 parity: "Details may vary" disclaimer */}
      {showDetailsVary && (
        <p className="text-xs text-muted-foreground/70 italic">
          Note: Details may vary by location. Confirm terms at retailer.
        </p>
      )}
      
      {/* Debug info */}
      {debugMode && (
        <div className="text-[10px] text-muted-foreground/50 border-t border-border/50 pt-1 mt-1">
          Source: {deal.raw_source_notes ? 'raw_source_notes' : deal.fine_print ? 'fine_print' : 'headline_mechanic'}
          {showDetailsVary && ' | Details vary: true'}
        </div>
      )}
    </div>
  );
};

export default TermsBlock;
